// The ferry and the public airship: they keep to a timetable on the real clock, so every player sees them in the
// same place at the same moment (nothing is sent over the network). A preview for now: you can watch them go,
// and boarding (with passes or allegiance rank) comes with the nations.
export type VesselKind = 'ship' | 'airship';
export interface RouteStop { name: string; i: number }
export interface Route {
  id: string; kind: VesselKind; name: string;
  /** Overworld tiles, a closed loop (the last point joins the first). */
  path: [number, number][];
  /** Where it waits (indexes into the path). */
  stops: RouteStop[];
  /** Tiles a second, and seconds at each stop. */
  speed: number; dwell: number;
}

const there = (out: [number, number][]): [number, number][] => [...out, ...out.slice(1, -1).reverse()];   // out and back again
const FERRY_OUT: [number, number][] = [[18, 43], [17, 47], [26, 49], [40, 49], [52, 48], [62, 47], [70, 45], [74, 40], [75, 35], [76, 31]];

export const ROUTES: Route[] = [
  { id: 'ferry', kind: 'ship', name: 'The Gull Ferry', path: there(FERRY_OUT),
    stops: [{ name: 'Riverside Landing', i: 0 }, { name: 'Eastreach Pier', i: FERRY_OUT.length - 1 }], speed: 1.1, dwell: 25 },
  { id: 'airship', kind: 'airship', name: 'The Skylark', path: [[40, 25], [52, 29], [64, 31], [66, 20], [62, 10], [52, 11], [42, 13], [38, 19]],
    stops: [{ name: 'Tiny Town', i: 0 }, { name: 'Sky Dock', i: 2 }, { name: "Dragon's Spine", i: 4 }, { name: 'Rustwind Crags', i: 6 }], speed: 1.6, dwell: 18 },
];

export interface VesselView {
  id: string; kind: VesselKind; name: string;
  /** Where it is (tiles) and which way it's heading (radians; 0 = east). */
  x: number; y: number; ang: number;
  /** Waiting at a stop, or on its way to one. */
  docked: string | null; heading: string;
  /** Seconds until it is next at each stop (0 while it's there). */
  due: { stop: string; inSec: number }[];
}

interface Leg { from: number; t0: number; len: number; dwell: number }
/** A route's timetable: each segment's start time (seconds into the loop), and the loop's length. */
function timetable(r: Route): { legs: Leg[]; period: number } {
  const legs: Leg[] = []; let t = 0;
  for (let i = 0; i < r.path.length; i++) {
    const a = r.path[i], b = r.path[(i + 1) % r.path.length], dwell = r.stops.some(s => s.i === i) ? r.dwell : 0;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]) / r.speed;
    legs.push({ from: i, t0: t, len, dwell }); t += dwell + len;
  }
  return { legs, period: t };
}
const TABLES = new Map(ROUTES.map(r => [r.id, timetable(r)]));

/** Where every vessel is at this moment (ms since the epoch). The same answer on every screen. */
export function vesselsAt(ms: number): VesselView[] {
  return ROUTES.map(r => {
    const { legs, period } = TABLES.get(r.id)!, t = ((ms / 1000) % period + period) % period;
    const leg = legs.find((l, i) => t >= l.t0 && (i === legs.length - 1 || t < legs[i + 1].t0))!;
    const a = r.path[leg.from], b = r.path[(leg.from + 1) % r.path.length], ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
    const into = t - leg.t0, k = into <= leg.dwell ? 0 : Math.min(1, (into - leg.dwell) / Math.max(1e-6, leg.len));
    const stopHere = into <= leg.dwell ? r.stops.find(s => s.i === leg.from) : undefined;
    // the next stop along the path
    let nxt = r.stops[0];
    for (let j = 1; j <= r.path.length; j++) { const s = r.stops.find(x => x.i === (leg.from + j) % r.path.length); if (s) { nxt = s; break; } }
    const due = r.stops.map(s => {
      const at = legs[s.i].t0;   // it arrives at a stop when that stop's leg begins (then waits there)
      if (s === stopHere) return { stop: s.name, inSec: 0 };
      return { stop: s.name, inSec: Math.round(((at - t) % period + period) % period) };
    });
    return { id: r.id, kind: r.kind, name: r.name, x: a[0] + (b[0] - a[0]) * k, y: a[1] + (b[1] - a[1]) * k, ang, docked: stopHere?.name ?? null, heading: nxt.name, due };
  });
}

/** "in 2 min" / "now" for a timetable. */
export const dueText = (s: number): string => s <= 0 ? 'here now' : s < 60 ? `in ${s} s` : `in ${Math.round(s / 60)} min`;
